import { useState, useMemo } from 'react';
import { useTable, useSortBy, useGlobalFilter, Column } from 'react-table';
import BasicPagination from './BasicPagination';
import IconButtonActions from './IconButtonActions';
import SvgIcon from '../SvgIcon/SvgIcon';

interface BasicDataTableProps {
  columns: Column<any>[];
  data: any[];
  actions?: any;
  headerButton?: React.ReactNode;
  documentDropdown?: React.ReactNode;
  searchComponent?: React.ReactNode;
  noDataMessage?: string;
}

const BasicDataTable: React.FC<BasicDataTableProps> = ({
  columns,
  data,
  actions,
  headerButton,
  documentDropdown,
  searchComponent,
  noDataMessage = 'No hay registros disponibles.',
}) => {
  const [currentPage, setCurrentPage] = useState(0);

  const memoizedData = useMemo(() => data, [data]);
  const totalPages = Math.ceil(memoizedData.length / 5);
  const paginatedData = useMemo(
    () => memoizedData.slice(currentPage * 5, (currentPage + 1) * 5),
    [memoizedData, currentPage, 5]
  );

  const tableInstance = useTable(
    {
      columns: columns as readonly Column<any>[],
      data: paginatedData,
    },
    useGlobalFilter,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <section className="data-table-common data-table-two rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark">
      {(headerButton || documentDropdown) && (
        <div className="flex flex-wrap justify-between gap-4 border-b border-stroke px-4 pb-4 dark:border-strokedark md:px-8">
          {headerButton && <div>{headerButton}</div>}
          {documentDropdown && <div>{documentDropdown}</div>}
        </div>
      )}

      {searchComponent && (
        <div className="flex flex-wrap justify-between gap-4 border-b border-stroke px-4 pb-4 pt-4 dark:border-strokedark md:px-8">
          <div className="w-full md:w-1/3">{searchComponent}</div>
        </div>
      )}

      <div className="w-full overflow-x-auto">
        <table
          {...getTableProps()}
          className="datatable-table w-full min-w-[700px] table-auto border-collapse break-words md:table-fixed md:overflow-auto"
        >
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...rest } = headerGroup.getHeaderGroupProps();
              return (
                <tr
                  key={key}
                  {...rest}
                  className="bg-gray-100 dark:bg-gray-800"
                >
                  {headerGroup.headers.map((column) => {
                    const { key: colKey, ...colRest } = column.getHeaderProps(
                      column.getSortByToggleProps()
                    );
                    return (
                      <th
                        key={colKey}
                        {...colRest}
                        className="px-5 py-4 text-left text-sm font-semibold"
                      >
                        <div className="flex items-center">
                          {column.render('Header')}
                          <div className="ml-2 inline-flex flex-col space-y-[2px]">
                            <span className="inline-block">
                              <SvgIcon
                                name="chevron-up-mini"
                                width={10}
                                height={5}
                              />
                            </span>
                            <span className="inline-block">
                              <SvgIcon
                                name="chevron-down-mini"
                                width={10}
                                height={5}
                              />
                            </span>
                          </div>
                        </div>
                      </th>
                    );
                  })}

                  {actions && (
                    <th className="min-w-[120px] text-center">
                      <span className="flex justify-center">Acciones</span>
                    </th>
                  )}
                </tr>
              );
            })}
          </thead>

          <tbody {...getTableBodyProps()}>
            {rows.length > 0 ? (
              rows.map((row) => {
                prepareRow(row);
                const { key, ...rowRest } = row.getRowProps();
                return (
                  <tr
                    key={key}
                    {...rowRest}
                    className="dark:border-gray-700 border-b"
                  >
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellRest } = cell.getCellProps();
                      return (
                        <td
                          key={cellKey}
                          {...cellRest}
                          className="text-gray-900 dark:text-gray-200 px-4 py-2 text-sm"
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}

                    {actions && (
                      <td className="px-4 py-2 text-center">
                        <div className="flex justify-center">
                          <IconButtonActions
                            actions={actions}
                            row={row.original}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columns.length + (actions ? 1 : 0)}
                  className="py-4 text-center"
                >
                  {noDataMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <BasicPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </section>
  );
};

export default BasicDataTable;
